/**
 * @author       luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright    Copyright (c) 2023, Zumper
 */

import { useMountAndUnmount } from '@zcode-ui/hook'
import { Popover, PopoverContent, PopoverTrigger, cn } from '@zcode-ui/shadz-ui'
import React, { useCallback, type KeyboardEventHandler, type MouseEventHandler } from 'react'
import { Input, type InputProps } from '../Input/Input'
import arrowDown from './arrowDown.svg'

export interface OptionType<Value, DisplayValue extends string = string> {
  label: React.ReactNode
  value: Value
  displayValue: DisplayValue
  className?: string
}

export const DownArrow = ({
  isInverted,
}: {
  isInverted: boolean
}) => {
  return <img alt="" src={arrowDown} className={isInverted ? 'rotate-180' : undefined} />
}

export interface BaseInputWithPopoverProps {
  children?: React.ReactNode
  error?: string
  inputProps?: Omit<InputProps, 'label' | 'onBlur' | 'onClick'>
  inputLabel?: string
  isDefault?: boolean
  isHalfWidthOnTheRight?: boolean
  isInvalid?: boolean
  isOpen: boolean
  isRequired?: boolean
  name?: string
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onClose: () => void
  onRightElementClick?: () => void
  onClick?: MouseEventHandler<HTMLElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  popoverBodyClassName?: string
  popoverHeader?: React.ReactNode
  popoverHeaderClassName?: string
  popoverFooter?: React.ReactNode
  value: string
}

export const BaseInputWithPopover = ({
  children,
  error,
  isOpen,
  inputProps,
  inputLabel = '',
  isDefault,
  isHalfWidthOnTheRight,
  isInvalid,
  isRequired,
  name,
  onBlur,
  onClick,
  onClose,
  onKeyDown,
  popoverBodyClassName,
  popoverFooter,
  popoverHeader,
  popoverHeaderClassName,
  value,
}: BaseInputWithPopoverProps) => {
  const [renderPopover, setRenderPopover] = React.useState(false)
  useMountAndUnmount({
    onMount: () => {
      setRenderPopover(true)
    },
  })
  return (
    <Popover
      open={isOpen}
      onOpenChange={useCallback(
        (open: boolean) => {
          if (!open) {
            onClose()
          }
        },
        [onClose]
      )}
    >
      <div className="w-full [&_input]:cursor-pointer [&_div]:cursor-pointer">
        <PopoverTrigger asChild>
          <Input
            aria-expanded={isOpen}
            error={error}
            isDefault={isDefault}
            isInvalid={isInvalid}
            isRequired={isRequired}
            label={inputLabel}
            name={name}
            onBlur={onBlur}
            onClick={onClick}
            onKeyDown={onKeyDown}
            readOnly
            rightElement={<DownArrow isInverted={isOpen} />}
            role="combobox"
            value={value}
            {...inputProps}
            className={cn('text-left', inputProps?.className)}
          />
        </PopoverTrigger>
      </div>
      {renderPopover ? (
        <PopoverContent
          className="max-w-[324px] p-0 w-[324px] z-popover"
          role="presentation"
          // Figma wants the top of the popover to be 6px above the top of the dropdown/input
          // The -60 value takes care of moving the top of the popover from the bottom of the dropdown
          // up to the top of the dropdown and then 6px beyond that
          // https://www.figma.com/design/Qwg7CFiMgH3bgYEoAjDJKf/Building-Page-Redesign?node-id=7441-105663&t=mtqaBNtlYQRs75kl-1
          sideOffset={-60}
          {...(isHalfWidthOnTheRight
            ? // If a dropdown is 50% width and on the right, Figma wants the right edge of the popover
              // to be 6px to the right of the right edge of the dropdown
              // https://www.figma.com/design/Qwg7CFiMgH3bgYEoAjDJKf/Building-Page-Redesign?node-id=7441-105693&t=mtqaBNtlYQRs75kl-1
              // We need an extra 50px because "the width of the dropdown" in code doesn't include the 50px-wide
              // caret on the right side of the dropdown
              { align: 'end', alignOffset: -56 }
            : // If a dropdown is 50% width and on the left, Figma wants the left edge of the popover
              // to be 6px to the left of the left edge of the dropdown
              // https://www.figma.com/design/Qwg7CFiMgH3bgYEoAjDJKf/Building-Page-Redesign?node-id=7441-105699&t=mtqaBNtlYQRs75kl-1
              { align: 'start', alignOffset: -6 })}
        >
          {popoverHeader && (
            <header
              className={cn(
                'border-b border-b-gray-150 border-solid font-body font-bold py-5 text-center text-gray-800 text-sm tracking-[1.96px]',
                popoverHeaderClassName
              )}
            >
              {popoverHeader}
            </header>
          )}
          <div className={cn('px-0 py-2', popoverBodyClassName)}>{children}</div>
          {popoverFooter && (
            <footer className="border-solid border-t border-t-neutral-100 font-body font-bold py-5 text-center text-gray-800 text-sm">
              {popoverFooter}
            </footer>
          )}
        </PopoverContent>
      ) : null}
    </Popover>
  )
}
